/**
 * A Composable that autofocus an element, conditionally
 * It's designed be an Alpine.Magic (or Directive).
 *
 * It's based on an IntersectionObserver to detect & trigger when the input is visbile.
 * This is mandatory for input within modals.
 *
 * It supports 3 modes:
 * - 'none' : the input is never focused.
 * - 'all' : the input is focused when visible, without condition
 * - 'desktop-only': the input is focused when visible, when window.innerWidth > 1024
 *
 * How to use:
 * <input x-init="$autofocus.apply('desktop-only', $el)">
 */

export const useAutofocus = () => {
    const DESKTOP_BREAKPOINT = 1024;

    /**
     * A function to detect if we are in desktop version.
     * @returns {boolean} true if we are in desktop version, false otherwise.
     */
    const _isDesktop = () => window.innerWidth >= DESKTOP_BREAKPOINT;
    /**
     * A function that detect if all conditions are fulfilled to focus the element
     * @param {"none" | "all" | "desktop-only"} mode
     * @returns {boolean} true if the element should receive the focus, false otherwise.
     */
    const _shouldFocus = (mode) => {
        return mode === "all" || (mode === "desktop-only" && _isDesktop());
    };
    /**
     * A Function that set the cursor at the beginning of the element.
     * <!> If the element has "email" type, it'll be converted to text to reset the cursor,
     *   then switch back to email
     * @param {HTMLElement} element
     */
    const _setCursorToStart = (element) => {
        if (element.type == "email") {
            element.type = "text";
            element.setSelectionRange(0, 0);
            element.type = "email";
        } else {
            const supportedTypes = ["text", "search", "url", "tel", "password"];
            const inputType = element.type
                ? element.type.toLowerCase()
                : "text";
            if (
                element.setSelectionRange &&
                supportedTypes.includes(inputType)
            ) {
                element.setSelectionRange(0, 0);
            }
        }
    };

    /**
     * A function that focus the element during the next render.
     * @param {HTMLElement} element
     */
    const _focus = (element) =>
        setTimeout(() => {
            element.focus();
            _setCursorToStart(element);
        }, 0);
    /**
     *
     * @param {"none" | "all" | "desktop-only"} mode
     * @param {HTMLElement} element
     * @returns
     */
    const apply = (mode, element) => {
        // no element or always disabled, no further action
        if (!element || mode == "none") return;
        // element is already visible, no need to mutate, give the focus immediately
        if (
            element.offsetWidth > 0 &&
            element.offsetHeight > 0 &&
            _shouldFocus(mode)
        ) {
            _focus(element);
            return;
        }
        // element is not visible, observe when displayed
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (
                    entry.isIntersecting &&
                    entry.target.offsetWidth > 0 &&
                    entry.target.offsetHeight > 0 &&
                    _shouldFocus(mode)
                ) {
                    _focus(element);
                    // don't disconnect, otherwise it'll not focus again if element is hidden/visible/hidden/visible ...
                }
            });
        });
        observer.observe(element);
    };

    return {
        apply,
    };
};
